/**
 * The PRESENTATION of a report template (decisions D251–D253).
 *
 * ===========================================================================
 * A LAYOUT ARRANGES WHAT THE RECORD HOLDS. IT NEVER ADDS TEST SCIENCE.
 * ===========================================================================
 *
 * A report template chooses which sections a report prints, in which order,
 * and a handful of presentation options (logo, page numbers, QR, who signs).
 * It never defines a field, a unit, a formula or a limit: the "specialised
 * results" area is filled at render time by the TEST's own result template
 * (`result-templates.ts`) — ASTM D2216 today, others when each is validated
 * (`CLAUDE.md` section 4). A test with no specialised template prints its
 * general result there.
 *
 * The configuration is STRUCTURED, never free-form: a fixed set of known
 * sections and result elements, each visible or hidden, in an order the
 * laboratory chooses. Unknown keys are dropped and missing ones restored by
 * `normalizeReportLayout`, so a stored configuration can never name a section
 * the renderer does not know, nor silently lose one added later.
 *
 * An issued report freezes the NORMALISED configuration it was printed with
 * (`ReportSnapshot.template.layoutConfig`); editing a template afterwards
 * changes no issued document.
 */
import { IMPLEMENTED_RESULT_TEMPLATES } from './result-templates.js';
/** Document-level sections, printed top to bottom in the configured order. */
export const REPORT_SECTION = {
    /** Laboratory logo, name, address, contacts, registrations. */
    HEADER: 'header',
    /** Report control number, version, date reported, work order, client, project, location. */
    REPORT_INFORMATION: 'reportInformation',
    /** One block per reported result — see `REPORT_RESULT_ELEMENT`. */
    RESULTS: 'results',
    /** The report's own remarks, written when it was prepared. */
    REMARKS: 'remarks',
    /** Notes or disclaimer — from the template or the laboratory's settings. */
    NOTES: 'notes',
    /** The issuer and the verification QR. */
    VERIFICATION: 'verification',
};
export const ALL_REPORT_SECTIONS = Object.freeze([
    REPORT_SECTION.HEADER,
    REPORT_SECTION.REPORT_INFORMATION,
    REPORT_SECTION.RESULTS,
    REPORT_SECTION.REMARKS,
    REPORT_SECTION.NOTES,
    REPORT_SECTION.VERIFICATION,
]);
/**
 * The parts of ONE result's block, in the configured order. The order of the
 * product default follows the laboratory reports the owner supplied: sample
 * and dates, then the test-specific body, then equipment and environment,
 * then who tested, reviewed and approved.
 */
export const REPORT_RESULT_ELEMENT = {
    TEST_TITLE: 'testTitle',
    STANDARD_METHOD: 'standardMethod',
    SAMPLE_RECEIPT: 'sampleReceipt',
    DATES: 'dates',
    /** DYNAMIC: printed by the test's own result template, or the general result. */
    SPECIALISED_RESULTS: 'specialisedResults',
    EQUIPMENT: 'equipment',
    ENVIRONMENT: 'environment',
    SIGNATURES: 'signatures',
};
export const ALL_REPORT_RESULT_ELEMENTS = Object.freeze([
    REPORT_RESULT_ELEMENT.TEST_TITLE,
    REPORT_RESULT_ELEMENT.STANDARD_METHOD,
    REPORT_RESULT_ELEMENT.SAMPLE_RECEIPT,
    REPORT_RESULT_ELEMENT.DATES,
    REPORT_RESULT_ELEMENT.SPECIALISED_RESULTS,
    REPORT_RESULT_ELEMENT.EQUIPMENT,
    REPORT_RESULT_ELEMENT.ENVIRONMENT,
    REPORT_RESULT_ELEMENT.SIGNATURES,
]);
/** Where the Notes / Disclaimer section takes its text from. */
export const REPORT_NOTES_SOURCE = {
    NONE: 'none',
    /** The template's own bilingual notes text. */
    TEMPLATE: 'template',
    /** The laboratory's report footer text, from its Report settings. */
    LABORATORY: 'laboratory',
};
export const ALL_REPORT_NOTES_SOURCES = Object.freeze([
    REPORT_NOTES_SOURCE.NONE,
    REPORT_NOTES_SOURCE.TEMPLATE,
    REPORT_NOTES_SOURCE.LABORATORY,
]);
/**
 * The product's default layout: every section shown, in the order of the
 * reports the owner supplied. A product default, identical for every
 * laboratory and editable by each — not a laboratory's value (`CLAUDE.md` 6.1).
 * A template that never had a layout saved reads as this.
 */
export const DEFAULT_REPORT_LAYOUT = Object.freeze({
    version: 1,
    sections: ALL_REPORT_SECTIONS.map((key) => ({ key, visible: true })),
    resultElements: ALL_REPORT_RESULT_ELEMENTS.map((key) => ({ key, visible: true })),
    header: { showLogo: true, showAddress: true, showContacts: true, showRegistrations: true },
    reportInformation: {
        showReportNumber: true,
        showDateReported: true,
        showWorkOrder: true,
        showClient: true,
        showProject: true,
        showProjectLocation: true,
    },
    signatures: { testedBy: true, reviewedBy: true, approvedBy: true },
    notesSource: REPORT_NOTES_SOURCE.NONE,
    footer: { showFooterText: true, showReportIdentity: true, pageNumbering: true },
    showQr: true,
});
function isRecord(value) {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}
function orderedEntries(raw, known) {
    const seen = new Set();
    const out = [];
    if (Array.isArray(raw)) {
        for (const entry of raw) {
            if (!isRecord(entry))
                continue;
            const key = entry.key;
            if (!known.includes(key) || seen.has(key))
                continue;
            seen.add(key);
            out.push({ key, visible: entry.visible !== false });
        }
    }
    // A key the stored value does not name — one added after it was saved —
    // is appended visible, in the product order, rather than silently lost.
    for (const key of known)
        if (!seen.has(key))
            out.push({ key, visible: true });
    return out;
}
function flags(raw, defaults) {
    const source = isRecord(raw) ? raw : {};
    const out = {};
    for (const [name, fallback] of Object.entries(defaults)) {
        out[name] = typeof source[name] === 'boolean' ? source[name] : fallback;
    }
    return out;
}
/**
 * A complete, well-formed layout from anything stored or sent: `null` is the
 * product default, unknown keys are dropped, missing keys restored. Idempotent.
 */
export function normalizeReportLayout(raw) {
    const source = isRecord(raw) ? raw : {};
    const notesSource = ALL_REPORT_NOTES_SOURCES.includes(source.notesSource)
        ? source.notesSource
        : DEFAULT_REPORT_LAYOUT.notesSource;
    return {
        version: 1,
        sections: orderedEntries(source.sections, ALL_REPORT_SECTIONS),
        resultElements: orderedEntries(source.resultElements, ALL_REPORT_RESULT_ELEMENTS),
        header: flags(source.header, DEFAULT_REPORT_LAYOUT.header),
        reportInformation: flags(source.reportInformation, DEFAULT_REPORT_LAYOUT.reportInformation),
        signatures: flags(source.signatures, DEFAULT_REPORT_LAYOUT.signatures),
        notesSource,
        footer: flags(source.footer, DEFAULT_REPORT_LAYOUT.footer),
        showQr: typeof source.showQr === 'boolean' ? source.showQr : DEFAULT_REPORT_LAYOUT.showQr,
    };
}
/** Whether two layouts print the same document. */
export function sameReportLayout(left, right) {
    return JSON.stringify(normalizeReportLayout(left)) === JSON.stringify(normalizeReportLayout(right));
}
/** The visible keys of an ordered list, in order. */
export function visibleKeys(entries) {
    return entries.filter((entry) => entry.visible).map((entry) => entry.key);
}
/**
 * How a report's template was chosen when the preparer named none (D254):
 * the one template the selected tests name, otherwise the laboratory default.
 */
export const REPORT_TEMPLATE_RESOLUTION = {
    TEST_SPECIFIC: 'test_specific',
    LABORATORY_DEFAULT: 'laboratory_default',
    /**
     * The selected tests name different templates: the laboratory default is
     * used when there is one (D257); otherwise the preparer chooses.
     */
    CONFLICT: 'conflict',
    /** No test names one and the laboratory has no default. */
    NONE: 'none',
};
/**
 * What a template SAMPLE shows in its specialised-results area: the general
 * result's outline, or the outline of one implemented result template — its
 * real labels with placeholders, never a value (D255).
 */
export const REPORT_SAMPLE_GENERAL = 'general';
export const ALL_RESULT_TEMPLATES_FOR_SAMPLE = Object.freeze([
    REPORT_SAMPLE_GENERAL,
    ...IMPLEMENTED_RESULT_TEMPLATES,
]);
//# sourceMappingURL=report-layout.js.map